import type { RecipeProposal } from './recipeProposal';
import type { ExpenseDraft } from './receipts';
import type { CatalogPricesProposal } from './catalogPrices';
import type { Period } from './period';
import type { PurchaseDocumentType } from './types';
import type { Unit } from './units';
import type { ExpenseInput } from './validation';

export type DraftSource = 'whatsapp' | 'email' | 'app' | 'demo';
export type DraftStatus = 'pending' | 'confirmed' | 'discarded' | 'expired';
/**
 * Respuesta a una pregunta de precio. `unitPrice` es el precio de `qty`
 * `purchaseUnit` (el formato de compra); los borradores antiguos no traen
 * `qty` y valen por 1 `unit`.
 */
export interface AnswerProposal { kind: 'confirm-price'; ingredientId: string; unitPrice: number; unit: Unit; questionId: string; qty?: number; purchaseUnit?: Unit; presentation?: string }
export interface DraftBase {
  id: string;
  tenantId: string;
  locationId?: string;
  source: DraftSource;
  createdBy: { userSub?: string; phone?: string; email?: string };
  status: DraftStatus;
  inputKey?: string;
  /** Server-controlled original upload/receipt time, before queues and OCR. Missing on legacy drafts. */
  inputReceivedAt?: string;
  summary: string;
  createdAt: string;
  expiresAt: number;
  confirmedAt?: string;
  lastCorrectionId?: string;
  result?: {
    recipeId?: string; expenseId?: string; ingredientIds?: string[];
    /**
     * Bloque S: precios que tenía cada ingrediente antes de «Guardar las N»
     * (`puesto` = el que dejó la factura). «Deshacer» los devuelve si nadie
     * los cambió después.
     */
    preciosAntes?: Array<{ ingredientId: string; netPrice?: number; purchaseQty?: number; purchaseUnit?: string; puesto?: number }>;
  };
}
export type Draft = DraftBase & (
  | { kind: 'expense'; payload: { proposal: ExpenseDraft; input?: ExpenseInput; documentType?: PurchaseDocumentType; requiresReview?: boolean } }
  | { kind: 'recipe'; payload: RecipeProposal }
  | { kind: 'catalog-prices'; payload: CatalogPricesProposal }
  | { kind: 'answer'; payload: AnswerProposal }
);

export const DRAFT_TTL_SECONDS = 7 * 24 * 60 * 60;
export function draftIsExpired(draft: Pick<DraftBase, 'expiresAt' | 'status'>, now = Date.now()): boolean {
  return draft.status === 'expired' || draft.expiresAt <= Math.floor(now / 1000);
}
export function draftDomainPath(kind: Draft['kind']): string {
  return kind === 'expense' ? '/v1/expenses' : kind === 'recipe' ? '/v1/recipes/proposal/confirm' : '/v1/import/ingredients';
}

/** Conversación privada de cada miembro; los borradores mantienen su confirmación habitual. */
/**
 * Un mensaje a Alexia. Es UNA conversación: lo que cuentas (un plato, un
 * gasto, una factura) acaba en borradores, y lo que preguntas (cómo va el
 * negocio, cómo se hace algo) se responde con tus datos. El periodo y el
 * local acotan las respuestas de análisis, igual que en el resto de la app.
 */
export interface InboxInput {
  /** Opt-in durable processing outside the HTTP request. Legacy clients remain synchronous. */
  execution?: 'async';
  id: string;
  text: string;
  fileName?: string;
  draftIds?: string[];
  replyToDraftId?: string;
  period?: Period;
  locationId?: string;
}
/** Qué consultó Alexia para responder: se enseña plegado bajo la respuesta. */
export interface InboxToolCall { name: string; summary: string; input?: unknown }
export interface InboxEntry extends InboxInput {
  createdAt: string;
  expiresAt: number;
  reply?: string;
  /** No automatic retry: an unfinished attempt stays associated with this message id. */
  processing?: { status: 'queued' | 'processing' | 'failed'; retryable?: boolean; error?: string };
  /** `analysis` = respuesta con tus datos; `proposals` = borradores; `notice` = aviso sin más. */
  replyKind?: 'analysis' | 'proposals' | 'notice';
  toolCalls?: InboxToolCall[];
  cards: Array<{ draft: Draft; text: string }>;
}
